import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Copy, Check, Plus, X, Save, ShieldAlert, ImagePlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getEpkByUser, updateEpk, getReleases, uploadEpkImage, resolveAsset } from "@/lib/afmApi";
import { Loading, SectionTitle } from "@/components/common";

const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-black";
const Label = ({ children }) => <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">{children}</label>;

const MAX_BYTES = 8 * 1024 * 1024;
const toBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(String(r.result).split(",")[1]);
  r.onerror = rej;
  r.readAsDataURL(file);
});

export default function Epk() {
  const { user } = useAuth();
  const [epk, setEpk] = useState(null);
  const [status, setStatus] = useState("loading");
  const [releases, setReleases] = useState([]);
  const [saving, setSaving] = useState(false);
  const [denied, setDenied] = useState(false);
  const [copied, setCopied] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    getEpkByUser(user.id).then((d) => { setEpk(d); setStatus("ok"); }).catch(() => setStatus("error"));
    getReleases(user.id).then(setReleases).catch(() => {});
  }, [user.id]);

  if (status === "loading") return <Loading />;
  if (status === "error" || !epk) return <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500" data-testid="epk-missing">Aún no tienes un EPK configurado. Contacta a tu equipo de distribución para activarlo.</div>;

  const set = (k, v) => setEpk((p) => ({ ...p, [k]: v }));
  const list = (k) => (Array.isArray(epk[k]) ? epk[k] : []);
  const addItem = (k) => set(k, [...list(k), ""]);
  const setItem = (k, i, v) => set(k, list(k).map((x, idx) => (idx === i ? v : x)));
  const delItem = (k, i) => set(k, list(k).filter((_, idx) => idx !== i));

  const publicUrl = `${window.location.origin}/epk/${epk.slug}`;
  const copy = () => { navigator.clipboard.writeText(publicUrl); setCopied(true); toast.success("Enlace copiado"); setTimeout(() => setCopied(false), 1600); };

  const uploadFile = async (file) => {
    if (!file) return null;
    if (file.size > MAX_BYTES) { toast.error("La imagen supera el máximo de 8MB"); return null; }
    setUploading(true);
    try {
      const b64 = await toBase64(file);
      const res = await uploadEpkImage(user.id, file.type, b64);
      return res?.url || null;
    } catch {
      toast.error("No se pudo subir la imagen");
      return null;
    } finally { setUploading(false); }
  };

  const onAvatar = async (e) => {
    const url = await uploadFile(e.target.files?.[0]);
    if (url) { set("avatar_url", url); toast.success("Avatar actualizado — recuerda Guardar"); }
    e.target.value = "";
  };

  const onAddPhoto = async (e) => {
    const url = await uploadFile(e.target.files?.[0]);
    if (url) set("photos", [...list("photos"), url]);
    e.target.value = "";
  };

  const save = async () => {
    setSaving(true);
    setDenied(false);
    try {
      const payload = {
        viewer_id: user.id,
        name: epk.name,
        bio: epk.bio,
        theme: epk.theme,
        avatar_url: epk.avatar_url,
        press: list("press").filter(Boolean),
        photos: list("photos").filter(Boolean),
        featured_release_id: epk.featured_release_id,
        published: epk.published,
      };
      const res = await updateEpk(user.id, payload);
      setEpk((p) => ({ ...p, ...res }));
      toast.success("EPK actualizado");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "string" && detail.toLowerCase().includes("permiso")) {
        setDenied(true);
        toast.error("Tu cuenta no tiene permiso para editar el EPK todavía.");
      } else {
        toast.error(detail || "No se pudo guardar el EPK");
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="epk-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-5">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Tu EPK público</div>
          <div className="mt-1 font-mono text-sm text-slate-700">{publicUrl}</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={copy} data-testid="copy-epk-link" className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-semibold hover:border-black">
            {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />} Copiar
          </button>
          <a href={`/epk/${epk.slug}`} target="_blank" rel="noopener noreferrer" data-testid="open-epk-public" className="flex items-center gap-1.5 rounded-lg bg-black px-3 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">
            <ExternalLink className="h-4 w-4" /> Ver público
          </a>
        </div>
      </div>

      {denied && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800" data-testid="epk-permission-note">
          <ShieldAlert className="h-4 w-4 shrink-0" /> Tu cuenta no tiene permiso para editar el EPK por ahora. La vista pública funciona; la edición se habilitará desde tu equipo de distribución.
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <SectionTitle>Perfil del artista</SectionTitle>
        <div className="grid gap-5 sm:grid-cols-2">
          <div><Label>Nombre</Label><input data-testid="epk-name-input" value={epk.name || ""} onChange={(e) => set("name", e.target.value)} className={inputCls} /></div>
          <div>
            <Label>Tema</Label>
            <select data-testid="epk-theme-input" value={epk.theme || "dark"} onChange={(e) => set("theme", e.target.value)} className={inputCls}>
              <option value="dark">Oscuro</option>
              <option value="light">Claro</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <Label>Avatar</Label>
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-slate-50">
                {epk.avatar_url ? <img src={resolveAsset(epk.avatar_url)} alt="avatar" className="h-full w-full object-cover" /> : <ImagePlus className="h-5 w-5 text-slate-300" />}
              </div>
              <label className="cursor-pointer rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold transition-colors hover:border-black" data-testid="epk-avatar-upload">
                {uploading ? "Subiendo…" : "Subir avatar"}
                <input type="file" accept="image/*" className="hidden" onChange={onAvatar} disabled={uploading} />
              </label>
              {epk.avatar_url && <button type="button" onClick={() => set("avatar_url", "")} data-testid="epk-avatar-remove" className="text-sm text-slate-500 hover:text-red-500">Quitar</button>}
            </div>
          </div>
          <div className="sm:col-span-2"><Label>Biografía</Label><textarea data-testid="epk-bio-input" rows={4} value={epk.bio || ""} onChange={(e) => set("bio", e.target.value)} className={`${inputCls} resize-none`} /></div>
          <div className="sm:col-span-2">
            <Label>Lanzamiento destacado</Label>
            <select data-testid="epk-featured-input" value={epk.featured_release_id || ""} onChange={(e) => set("featured_release_id", e.target.value)} className={inputCls}>
              <option value="">Ninguno</option>
              {releases.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <SectionTitle right={<button onClick={() => addItem("press")} data-testid="epk-add-press" className="flex items-center gap-1 text-sm font-semibold text-[#AC3333] hover:text-[#FF0505]"><Plus className="h-4 w-4" /> Añadir</button>}>Prensa</SectionTitle>
        <div className="space-y-2">
          {list("press").length === 0 && <p className="text-sm text-slate-400">Sin menciones de prensa.</p>}
          {list("press").map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <input data-testid={`epk-press-${i}`} value={typeof p === "string" ? p : p.quote || ""} onChange={(e) => setItem("press", i, e.target.value)} className={inputCls} placeholder="Ej. Rolling Stone" />
              <button onClick={() => delItem("press", i)} className="rounded-lg border border-slate-300 p-2.5 text-slate-500 hover:border-red-400 hover:text-red-500"><X className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <SectionTitle right={<label className="flex cursor-pointer items-center gap-1 text-sm font-semibold text-[#AC3333] hover:text-[#FF0505]" data-testid="epk-add-photo"><Plus className="h-4 w-4" /> {uploading ? "Subiendo…" : "Añadir foto"}<input type="file" accept="image/*" className="hidden" onChange={onAddPhoto} disabled={uploading} /></label>}>Galería</SectionTitle>
        {list("photos").length === 0 ? (
          <p className="text-sm text-slate-400">Sin fotos. Sube imágenes de alta calidad para tu press kit.</p>
        ) : (
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
            {list("photos").map((ph, i) => {
              const u = typeof ph === "string" ? ph : ph.url;
              return (
                <div key={i} className="group relative aspect-square overflow-hidden rounded-lg border border-slate-200" data-testid={`epk-photo-${i}`}>
                  <img src={resolveAsset(u)} alt={`foto-${i}`} className="h-full w-full object-cover" />
                  <button type="button" onClick={() => delItem("photos", i)} className="absolute right-1 top-1 rounded-md bg-black/60 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100" data-testid={`epk-photo-del-${i}`}><X className="h-3.5 w-3.5" /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input data-testid="epk-published-input" type="checkbox" checked={!!epk.published} onChange={(e) => set("published", e.target.checked)} className="h-4 w-4 rounded border-slate-300" />
          EPK publicado
        </label>
        <button onClick={save} disabled={saving} data-testid="save-epk-button" className="flex items-center gap-2 rounded-lg bg-[#AC3333] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#FF0505] disabled:opacity-50">
          <Save className="h-4 w-4" /> {saving ? "Guardando…" : "Guardar EPK"}
        </button>
      </div>
    </div>
  );
}
